import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import printJS from 'print-js';
import head from 'lodash/head';
import compact from 'lodash/compact';
import uniqueId from 'lodash/uniqueId';
import isNil from 'lodash/isNil';
import {
  MultiMediaResponse,
  MultiMediaResponseDTO,
  type None,
  PrintClaimDTO,
  SignPatientMultimediaRequestDTO,
} from 'dtos';
import {
  getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
  postApiClaimPrintClaim,
  postApiMultimediaSignPatientMultimedia,
  postApiMultimediaBulkUploadUserMultimedia,
} from 'endpoints';
import {
  useHandleMediaUploadResponse,
  useUploadBulkMultimedia,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  MediaOriginatorType,
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  useMedia,
  useNotify,
} from 'core/hooks';
import {
  getErrorMessage,
} from 'utils/errors';

export function usePrintMediaRequest<T extends any[]>(
  queryFn: (...params: T) => Promise<MultiMediaResponseDTO>,
) {
  const notify = useNotify();
  const [queryKey, setQueryKey] = useState<[uniq: string, ...params: T] | null>(null);
  const requested = !isNil(queryKey);
  const {
    data,
    error,
    isError,
    isFetching,
  } = useQuery<MultiMediaResponseDTO, Error>({
    queryKey: queryKey ?? [],
    queryFn: async () => {
      const [, ...params] = queryKey ?? [];
      const response = await queryFn(...params as T);
      return response;
    },
    enabled: !isNil(queryKey),
  });

  useEffect(() => {
    if (!isFetching && !isNil(data) && requested) {
      const media = head(data?.list);
      const dataUrl = media?.dataUrl;
      const isBase64 = dataUrl?.startsWith('data:') ?? false;
      printJS({
        printable: isBase64
          ? dataUrl?.slice(dataUrl.indexOf(',') + 1)
          : media?.url?.value,
        type: 'pdf',
        base64: isBase64,
        showModal: true,
      });
      setQueryKey(null);
    }
  }, [data, isFetching, queryKey]);

  useEffect(() => {
    if (!isFetching && isError && requested) {
      notify({
        variant: 'error',
        message: getErrorMessage(error) ?? 'Failed to print the document. Please try again later.',
      });
      setQueryKey(null);
    }
  }, [error, isError, isFetching, notify, requested]);

  return {
    isPrinting: isFetching,
    print: (params: T) => {
      setQueryKey([uniqueId('print-request__'), ...params]);
    },
  };
}

export function usePrintClaim():
  UseMutationResult<MultiMediaResponseDTO, Error, PrintClaimDTO> {
  return useMutation<MultiMediaResponseDTO, Error, PrintClaimDTO>({
    mutationFn: (data) => postApiClaimPrintClaim(data),
  });
}

export function useSignPatientMultimedia():
  UseMutationResult<None, Error, SignPatientMultimediaRequestDTO> {
  return useMutation<None, Error, SignPatientMultimediaRequestDTO>({
    mutationFn: (data) => postApiMultimediaSignPatientMultimedia(data),
  });
}

export function usePatientGetMultimediaByMedia(
  media: MultiMediaResponse,
): UseQueryResult<MultiMediaResponseDTO, Error> {
  const id = (isNil(media?.url) && media?.mediaId) ? media?.mediaId : 0;
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['multimedia', id],
    queryFn: () => getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId(id),
    enabled: !isNil(media) && id > 0,
  });
}

type UseResolvePatientMultimediaOptions = {
  mediaIds?: number[];
  allowedMimeTypes?: string[];
};

function getUniqueMediaIds(mediaIds: number[] = []) {
  const uniqueMediaIds = Array.from(new Set(compact(mediaIds)));
  return uniqueMediaIds;
}

function getValidResolvedMediaIds(
  mediaList: MultiMediaResponse[] = [],
  allowedMimeTypes: string[] = [],
) {
  const allowedMimeTypesSet = new Set(allowedMimeTypes);
  const validResolvedMediaIds = mediaList
    .filter((media) => {
      const isMimeTypeAllowed = allowedMimeTypesSet.size === 0
        || allowedMimeTypesSet.has(media.mimeType ?? '');
      return isMimeTypeAllowed;
    })
    .map((media) => media.mediaId)
    .filter((mediaId): mediaId is number => !isNil(mediaId));
  return validResolvedMediaIds;
}

export function useResolvePatientMultimedia({
  mediaIds = [],
  allowedMimeTypes = [],
}: UseResolvePatientMultimediaOptions) {
  const resolvedMediaIds = useMemo(() => getUniqueMediaIds(mediaIds), [mediaIds]);

  const query = useQuery<MultiMediaResponse[], Error>({
    queryKey: ['multimedia', 'resolve-many', resolvedMediaIds],
    queryFn: async () => {
      const responses = await Promise.all(
        resolvedMediaIds.map((mediaId) => (
          getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId(mediaId)
        )),
      );
      const mediaList = responses.map((response) => head(response.list)).filter(
        (media): media is MultiMediaResponse => !isNil(media),
      );
      return mediaList;
    },
    enabled: resolvedMediaIds.length > 0,
  });

  const resolvedMediaList = useMemo(() => query.data ?? [], [query.data]);
  const validResolvedMediaIds = useMemo(
    () => getValidResolvedMediaIds(resolvedMediaList, allowedMimeTypes),
    [allowedMimeTypes, resolvedMediaList],
  );

  return {
    ...query,
    mediaList: resolvedMediaList,
    validMediaIds: validResolvedMediaIds,
  };
}

export default function useUploadProfilePicture(
  id: number,
  originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
) {
  const { invalidateMedia } = useMedia();
  const { mutateAsync: uploadPatientBulk, isPending: isUploadingPatient } = useUploadBulkMultimedia();
  const { handleMediaUploadResponse } = useHandleMediaUploadResponse();

  const { mutateAsync: uploadUserBulk, isPending: isUploadingUser } = useMutation({
    mutationFn: (data: FileLocalUrl[]) => {
      const file = head(data);
      return postApiMultimediaBulkUploadUserMultimedia({
        userId: id,
        mediaList: [
          {
            multimediaType: MultimediaType.UserProfilePicture,
            fileName: file?.file?.name ?? file?.name ?? 'profile-picture.jpg',
            dataUrl: file?.dataUrl ?? '',
          },
        ],
      });
    },
  });

  const isUser = originatorType === MediaOriginatorType.User;
  const isPending = isUser ? isUploadingUser : isUploadingPatient;

  const onUpdateProfilePicture = async (data: FileLocalUrl[]) => {
    const response = isUser
      ? await uploadUserBulk(data)
      : await uploadPatientBulk({
        patientId: id,
        mediaList: data.map(({ dataUrl, file }) => ({
          multimediaType: MultimediaType.ProfilePicture,
          dataUrl: dataUrl ?? '',
          fileName: file?.name ?? '',
        })),
      });

    const uploadedSuccessfully = handleMediaUploadResponse(
      response,
      'Profile picture uploaded successfully!',
      'Failed to upload profile picture',
    );

    if (uploadedSuccessfully) {
      invalidateMedia(id ?? 0, originatorType);
    }
  };

  return { onUpdateProfilePicture, isPending };
}

export function useUploadUserProfilePicture(userId: number) {
  return useUploadProfilePicture(userId, MediaOriginatorType.User);
}

/**
 * Custom hook to play camera shutter sound
 * @returns Function to play the shutter sound
 *
 * @example
 * ```tsx
 * const playShutterSound = useShutterSound();
 *
 * // Play sound on capture
 * playShutterSound();
 * ```
 */
export function useShutterSound() {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Create audio element once
    audioRef.current = new Audio('/camera-shutter.mp3');
    audioRef.current.volume = 0.5; // Adjust volume (0.0 to 1.0)
    audioRef.current.preload = 'auto';

    return () => {
      // Cleanup on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playShutterSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0; // Reset to start for rapid captures
      audioRef.current.play().catch((error) => {
        // Silently fail if audio can't play (e.g., user hasn't interacted)
        console.warn('Could not play shutter sound:', error);
      });
    }
  }, []);

  return playShutterSound;
}
